<template>
  <section class="stickiness-card" aria-label="Stickiness score">
    <h3 class="stickiness-card__title">Stickiness score</h3>

    <div class="stickiness-card__value-row">
      <span class="stickiness-card__score">{{ displayScore }}</span>
      <span class="stickiness-card__denominator">/ 100</span>
      <span class="stickiness-card__badge" :class="`stickiness-card__badge--${band}`">
        {{ bandLabel }}
      </span>
    </div>

    <div
      class="stickiness-card__progress"
      role="progressbar"
      :aria-valuenow="clampedScore"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="`Stickiness score ${displayScore} out of 100`"
    >
      <div class="stickiness-card__progress-fill" :style="{ width: `${clampedScore}%` }"></div>
    </div>

    <p
      v-if="trendLine"
      class="stickiness-card__trend"
      :class="`stickiness-card__trend--${trendDirection}`"
    >
      {{ trendLine }}
    </p>

    <p v-if="formulaHint" class="stickiness-card__formula">{{ formulaHint }}</p>
  </section>
</template>

<script setup>
import { computed, defineProps } from "vue";

const props = defineProps({
  /** 0–100 score from the snapshot. */
  score: {
    type: [Number, String],
    default: 0,
  },
  /** sticky | watch | stalling (normalized by the tab's mapper). */
  band: {
    type: String,
    default: "stalling",
  },
  /** Signed trend in points vs the previous 30 days (0 = flat). */
  trend: {
    type: [Number, String],
    default: 0,
  },
  /** Prebuilt formula hint line (weights + band cut-offs from the config version). */
  formulaHint: {
    type: String,
    default: "",
  },
});

const BAND_LABELS = {
  sticky: "Sticky",
  watch: "Watch",
  stalling: "Stalling",
};

const bandLabel = computed(() => BAND_LABELS[props.band] || BAND_LABELS.stalling);

const displayScore = computed(() => {
  const n = Number(props.score);
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n));
});

const clampedScore = computed(() => {
  const n = Number(props.score);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
});

const trendDirection = computed(() => {
  const n = Number(props.trend);
  if (!Number.isFinite(n) || n === 0) return "flat";
  return n > 0 ? "up" : "down";
});

const trendLine = computed(() => {
  const n = Number(props.trend);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  if (trendDirection.value === "up") return `▲ ${abs} pts vs previous 30 days`;
  if (trendDirection.value === "down") return `▼ ${abs} pts vs previous 30 days`;
  return `${abs} pts vs previous 30 days`;
});
</script>

<style scoped>
.stickiness-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 340px;
  max-width: 100%;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.stickiness-card__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.stickiness-card__value-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stickiness-card__score {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 48px;
  font-weight: 600;
  line-height: 60px;
}

.stickiness-card__denominator {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.stickiness-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px 2px 8px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.stickiness-card__badge--sticky {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

.stickiness-card__badge--watch {
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
}

.stickiness-card__badge--stalling {
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
}

.stickiness-card__progress {
  height: 8px;
  border-radius: 8px;
  background: #eaecf0;
  overflow: hidden;
}

.stickiness-card__progress-fill {
  height: 100%;
  border-radius: 8px;
  background: #7f56d9;
}

.stickiness-card__trend {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.stickiness-card__trend--up {
  color: #067647;
}

.stickiness-card__trend--down {
  color: #b42318;
}

.stickiness-card__trend--flat {
  color: #667085;
}

.stickiness-card__formula {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}
</style>
